import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format, getDaysInMonth, startOfMonth } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getMonthTimecards } from '../../../services/timecardService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';

// Utility functions
const getLocalDateString = (date, includeDay = true) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return includeDay 
        ? localDate.toISOString().split('T')[0]
        : localDate.toISOString().split('T')[0].slice(0, 7);
};

const getLocalMonthStart = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return startOfMonth(localDate);
};


// Sticky column styles
const stickyColumnStyles = {
    project: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: 'background.paper',
        minWidth: '300px',
        whiteSpace: 'nowrap',
        borderRight: '2px solid rgba(224, 224, 224, 1)', // Thicker border as the only sticky column
        padding: '16px',
        '@media (max-width:600px)': {
            minWidth: '50%', // 50% of table width for mobile portrait
            maxWidth: '50%', // Prevent it from growing beyond 50%
            width: '50%', // Fixed width at 50%
            padding: '8px',
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }
    }
};

const MonthlyProjectsViewAll = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const { user, loading: authLoading } = useAuth();

    // Memoize currentMonthStart to prevent unnecessary recalculations
    const currentMonthStart = useMemo(() => getLocalMonthStart(currentDate), [currentDate]);

    // Fetch timecards when date changes
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchTimecardData = async () => {
            try {
                setDataLoading(true);
                const dateString = getLocalDateString(currentMonthStart, false);
                
                console.log('Fetching timecards:', {
                    dateString,
                    month: format(currentMonthStart, 'MMMM yyyy', { locale: enUS }),
                    userRole: user?.role,
                    viewType: 'all'
                });
                
                const data = await getMonthTimecards(dateString, null, 'all');
                if (mounted) {
                    console.log('Raw timecard data received:', {
                        count: data.length,
                        month: format(currentMonthStart, 'MMMM yyyy', { locale: enUS })
                    });
                    
                    // Filter timecards based on user role
                    let filteredTimecards = [...data];
                    
                    // If user is a team lead, filter to only show projects from their teams
                    if (user.role === 'team_lead') {
                        console.log('User is a team lead, filtering timecards by project');
                        
                        // Get project IDs for the teams where the user is the team lead
                        let teamProjectIds = [];
                        
                        // Check if user has teams
                        if (user.teams && user.teams.length > 0) {
                            // Get only the teams where the user is the team lead
                            const leadTeams = user.teams.filter(team => 
                                String(team.teamLead) === String(user.id)
                            );
                            
                            if (leadTeams.length > 0) {
                                // Get all projects from these teams
                                const teamProjects = leadTeams.flatMap(team => team.projects || []);
                                
                                // Make sure we don't have duplicates
                                teamProjectIds = [...new Set(teamProjects.map(id => String(id)))];
                                
                                console.log('Team project IDs:', teamProjectIds);
                            }
                        }
                        
                        // If we have ledTeams, try to get projects from there
                        if (teamProjectIds.length === 0 && user.ledTeams && user.ledTeams.length > 0) {
                            console.log('Using ledTeams:', user.ledTeams);
                            
                            // Find the corresponding full teams for these ledTeams
                            const ledTeamIds = user.ledTeams.map(team => team._id);
                            
                            // Find the full team objects from the user.teams array
                            const fullLedTeams = user.teams ? user.teams.filter(team => 
                                ledTeamIds.some(id => String(id) === String(team._id))
                            ) : [];
                            
                            // Get all projects from these teams
                            const teamProjects = fullLedTeams.flatMap(team => team.projects || []);
                            
                            // Make sure we don't have duplicates
                            teamProjectIds = [...new Set(teamProjects.map(id => String(id)))];
                            
                            console.log('Team project IDs from ledTeams:', teamProjectIds);
                        }
                        
                        // Filter timecards to only show those for team projects
                        if (teamProjectIds.length > 0) {
                            filteredTimecards = filteredTimecards.filter(timecard => 
                                timecard.project && teamProjectIds.includes(String(timecard.project._id))
                            );
                            
                            console.log('Filtered timecards count:', filteredTimecards.length);
                        } else {
                            console.log('No team projects found, showing all timecards');
                        }
                    }
                    
                    // Aggregate hours by project
                    const projectMap = new Map();
                    
                    filteredTimecards.forEach(timecard => {
                        const projectId = timecard.project._id;
                        if (!projectMap.has(projectId)) {
                            projectMap.set(projectId, {
                                _id: projectId,
                                project: timecard.project,
                                days: timecard.days.map(day => ({ ...day, hours: parseFloat(day.hours) }))
                            });
                        } else {
                            const existingProject = projectMap.get(projectId);
                            timecard.days.forEach((day, index) => {
                                // Ensure the day exists at this index before trying to add hours
                                if (!existingProject.days[index]) {
                                    existingProject.days[index] = { ...day, hours: 0 };
                                }
                                existingProject.days[index].hours += parseFloat(day.hours || 0);
                            });
                        }
                    });

                    const aggregatedData = Array.from(projectMap.values());
                    console.log('Projects aggregated:', {
                        count: aggregatedData.length,
                        month: format(currentMonthStart, 'MMMM yyyy', { locale: enUS })
                    });
                    
                    setTimecards(aggregatedData);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching timecards:', err);
                if (mounted) {
                    setError(err.message);
                    setTimecards([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        // Only fetch if we have a user and they are admin/team lead/accountant
        if (user && (user.role === 'admin' || user.role === 'team_lead' || user.role === 'accountant')) {
            fetchTimecardData();
        }

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [currentMonthStart, user]);

    const handlePreviousMonth = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() - 1);
            return newDate;
        });
    };

    const handleNextMonth = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() + 1);
            return newDate;
        });
    };


    // Memoize month info calculation
    const monthInfo = useMemo(() => {
        const daysInMonth = getDaysInMonth(currentMonthStart);
        const days = Array.from({ length: daysInMonth }, (_, i) => {
            const date = new Date(currentMonthStart);
            date.setDate(i + 1);
            return {
                date: date.toISOString()
            };
        });
        
        return { days };
    }, [currentMonthStart]);

    const { days } = monthInfo;

    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data or insufficient permissions
    if (!user || (user.role !== 'admin' && user.role !== 'team_lead' && user.role !== 'accountant')) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">
                    {!user ? 'Authentication required' : 'Insufficient permissions'}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    Monthly View - All Projects
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            marginLeft: '-8px', // Move the entire month navigation widget to the left
                        }
                    }}>
                        <IconButton 
                            onClick={handlePreviousMonth}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <Typography sx={{
                            '@media (max-width:600px)': {
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }
                        }}>
                            {format(currentMonthStart, 'MMMM yyyy', { locale: enUS })}
                        </Typography>
                        <IconButton 
                            onClick={handleNextMonth}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    <ExportButton
                        viewType="monthly"
                        timecards={timecards}
                        isAllUsers={true}
                        viewMode="allProjects"
                        date={currentMonthStart}
                        user={{ username: 'all_projects' }}
                        mobileIconOnly={true}
                    />
                </Box>
            </Paper>

            {/* Timecard Table */}
            <TableContainer 
                component={Paper}
                sx={{
                    overflow: 'auto',
                    '& .MuiTable-root': {
                        minWidth: days.length * 100 + 300, // Account for project column (300px) + days
                        borderCollapse: 'separate',
                        borderSpacing: 0
                    }
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={stickyColumnStyles.project}>
                                Project
                            </TableCell>
                            {days.map((day, index) => {
                                const date = new Date(day.date);
                                return (
                                    <TableCell 
                                        key={index} 
                                        align="center"
                                        sx={{
                                            width: `${100 / days.length}%`,
                                            minWidth: '100px',
                                            padding: '16px',
                                            whiteSpace: 'nowrap',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {format(date, 'EEE', { locale: enUS })}<br />
                                        {format(date, 'MMM d', { locale: enUS })}
                                    </TableCell>
                                );
                            })}
                            <TableCell 
                                align="center"
                                sx={{
                                    minWidth: '100px',
                                    padding: '16px'
                                }}
                            >
                                Total
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timecards.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={days.length + 2} align="center">
                                    No timecard data found for this month
                                </TableCell>
                            </TableRow>
                        ) : (
                            timecards.map((timecard) => (
                                <TableRow key={timecard._id}>
                                    <TableCell sx={stickyColumnStyles.project}>
                                        {timecard.project.name}
                                    </TableCell>
                                    {days.map((day, index) => {
                                        const timecardDay = timecard.days.find(d => {
                                            const dayDate = new Date(d.date);
                                            return dayDate.getDate() === (index + 1);
                                        });
                                        return (
                                            <TableCell 
                                                key={index} 
                                                align="center"
                                                sx={{
                                                    minWidth: '100px',
                                                    padding: '16px'
                                                }}
                                            >
                                                {timecardDay ? timecardDay.hours.toFixed(2) : '0.00'}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            minWidth: '100px',
                                            padding: '16px'
                                        }}
                                    >
                                        {timecard.days.reduce((sum, day) => sum + day.hours, 0).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default MonthlyProjectsViewAll;
